/**
 * Alshallal Pastry Company HR System - Departments Admin Page
 * Phase 18.3.1: Department Management (HR-Controlled)
 * 
 * RBAC: HR Admin / Super Admin only
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import {
  Building2,
  Plus,
  Edit2,
  Archive,
  RefreshCw,
  Users,
  Loader2,
  AlertCircle,
  Check,
  X
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DepartmentsPage = () => {
  const { token, hasRole } = useAuth();
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  // State
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  // Dialog state
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, department: null });
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    code: ''
  });

  // RBAC check
  const canManage = hasRole('hr_manager', 'hr_admin', 'super_admin');

  // Fetch departments
  const fetchDepartments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_URL}/departments/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { include_inactive: includeInactive }
      });
      
      setDepartments(response.data.departments || []);
    } catch (err) {
      setError(err.response?.data?.detail?.error || 'Failed to load departments');
    } finally {
      setLoading(false);
    }
  }, [token, includeInactive]);

  useEffect(() => {
    if (canManage) {
      fetchDepartments();
    }
  }, [fetchDepartments, canManage]);

  // Create department
  const handleCreate = async () => {
    if (!formData.name_en.trim()) return;
    
    try {
      setSubmitting(true);
      
      await axios.post(`${API_URL}/departments/`, {
        name_en: formData.name_en.trim(),
        name_ar: formData.name_ar.trim() || null,
        code: formData.code.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCreateDialog(false);
      setFormData({ name_en: '', name_ar: '', code: '' });
      fetchDepartments();
    } catch (err) {
      setError(err.response?.data?.detail?.error || 'Failed to create department');
    } finally {
      setSubmitting(false);
    }
  };

  // Update department
  const handleUpdate = async () => {
    if (!formData.name_en.trim()) return;
    
    try {
      setSubmitting(true);
      
      await axios.patch(`${API_URL}/departments/${editDialog.department.id}`, {
        name_en: formData.name_en.trim(),
        name_ar: formData.name_ar.trim() || null,
        code: formData.code.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEditDialog({ open: false, department: null });
      setFormData({ name_en: '', name_ar: '', code: '' });
      fetchDepartments();
    } catch (err) {
      setError(err.response?.data?.detail?.error || 'Failed to update department');
    } finally {
      setSubmitting(false);
    }
  };

  // Deactivate/Reactivate department
  const handleToggleActive = async (dept) => {
    try {
      const endpoint = dept.is_active ? 'deactivate' : 'reactivate';
      await axios.post(`${API_URL}/departments/${dept.id}/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDepartments();
    } catch (err) {
      setError(err.response?.data?.detail?.error || 'Failed to update department status');
    }
  };

  // Open edit dialog
  const openEditDialog = (dept) => {
    setFormData({
      name_en: dept.name_en || '',
      name_ar: dept.name_ar || '',
      code: dept.code || ''
    });
    setEditDialog({ open: true, department: dept });
  };

  // Access denied
  if (!canManage) {
    return (
      <div className="p-6">
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-slate-900">
              {isRTL ? 'الوصول مرفوض' : 'Access Denied'}
            </h2>
            <p className="text-slate-500 mt-2">
              {isRTL 
                ? 'فقط مسؤول الموارد البشرية والمشرف العام يمكنهم إدارة الأقسام.'
                : 'Only HR Admin and Super Admin can manage departments.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn("p-6 space-y-6", isRTL && "rtl")}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="h-7 w-7 text-blue-600" />
            {isRTL ? 'إدارة الأقسام' : 'Department Management'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isRTL ? 'إنشاء وإدارة أقسام المؤسسة' : 'Create and manage organizational departments'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchDepartments}>
            <RefreshCw className={cn("h-4 w-4 me-2", loading && "animate-spin")} />
            {isRTL ? 'تحديث' : 'Refresh'}
          </Button>
          <Button onClick={() => setCreateDialog(true)}>
            <Plus className="h-4 w-4 me-2" />
            {isRTL ? 'قسم جديد' : 'New Department'}
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ms-auto text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch
                id="include-inactive"
                checked={includeInactive}
                onCheckedChange={setIncludeInactive}
              />
              <Label htmlFor="include-inactive" className="text-sm text-slate-600">
                {isRTL ? 'عرض الأقسام غير النشطة' : 'Show inactive departments'}
              </Label>
            </div>
            <Badge variant="secondary">
              {departments.length} {isRTL ? 'أقسام' : 'departments'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : departments.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Building2 className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>{isRTL ? 'لا توجد أقسام' : 'No departments found'}</p>
              <Button className="mt-4" onClick={() => setCreateDialog(true)}>
                <Plus className="h-4 w-4 me-2" />
                {isRTL ? 'إنشاء قسم' : 'Create Department'}
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{isRTL ? 'الاسم (إنجليزي)' : 'Name (English)'}</TableHead>
                  <TableHead>{isRTL ? 'الاسم (عربي)' : 'Name (Arabic)'}</TableHead>
                  <TableHead>{isRTL ? 'الرمز' : 'Code'}</TableHead>
                  <TableHead>{isRTL ? 'الموظفون' : 'Employees'}</TableHead>
                  <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                  <TableHead className="text-end">{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {departments.map((dept) => (
                  <TableRow key={dept.id} className={!dept.is_active ? 'opacity-60' : ''}>
                    <TableCell className="font-medium">{dept.name_en}</TableCell>
                    <TableCell>{dept.name_ar || '-'}</TableCell>
                    <TableCell>
                      {dept.code ? (
                        <Badge variant="outline">{dept.code}</Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-slate-400" />
                        {dept.employee_count || 0}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={dept.is_active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}>
                        {dept.is_active 
                          ? (isRTL ? 'نشط' : 'Active')
                          : (isRTL ? 'غير نشط' : 'Inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-end">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(dept)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleToggleActive(dept)}
                          className={dept.is_active ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}
                        >
                          {dept.is_active ? (
                            <Archive className="h-4 w-4" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'إنشاء قسم جديد' : 'Create New Department'}</DialogTitle>
            <DialogDescription>
              {isRTL ? 'أضف قسمًا جديدًا للمؤسسة' : 'Add a new department to the organization'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'الاسم (إنجليزي) *' : 'Name (English) *'}</Label>
              <Input
                value={formData.name_en}
                onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                placeholder={isRTL ? 'مثال: الموارد البشرية' : 'e.g. Human Resources'}
              />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'الاسم (عربي)' : 'Name (Arabic)'}</Label>
              <Input
                value={formData.name_ar}
                onChange={(e) => setFormData(prev => ({ ...prev, name_ar: e.target.value }))}
                placeholder="مثال: الموارد البشرية"
                dir="rtl"
              />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'الرمز (اختياري)' : 'Code (optional)'}</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                placeholder={isRTL ? 'مثال: HR' : 'e.g. HR'}
                maxLength={20}
              />
              <p className="text-xs text-slate-500">
                {isRTL ? 'رمز فريد للقسم (اختياري)' : 'Unique code for the department (optional)'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialog(false); setFormData({ name_en: '', name_ar: '', code: '' }); }}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleCreate} disabled={submitting || !formData.name_en.trim()}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (isRTL ? 'إنشاء' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, department: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'تعديل القسم' : 'Edit Department'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'الاسم (إنجليزي) *' : 'Name (English) *'}</Label>
              <Input
                value={formData.name_en}
                onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'الاسم (عربي)' : 'Name (Arabic)'}</Label>
              <Input
                value={formData.name_ar}
                onChange={(e) => setFormData(prev => ({ ...prev, name_ar: e.target.value }))}
                dir="rtl"
              />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'الرمز' : 'Code'}</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                maxLength={20}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, department: null })}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleUpdate} disabled={submitting || !formData.name_en.trim()}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (isRTL ? 'حفظ' : 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DepartmentsPage;
